import deleteProductCategoryApi from '@/api/delete/company/delete-product-category';
import getProductCategoriesApi from '@/api/get/company/get-product-categories';
import getProductCategoryApi from '@/api/get/company/get-product-category';
import updateProductCategoryApi from '@/api/patch/user/update-product-category';
import createProductCategoryApi from '@/api/post/company/create-product-category';
import Alert from '@/components/alert/alert-component';
import { PrimaryButton } from '@/components/button/button-component';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import CreateEditModal from '@/components/modal/modal-component';
import PageWrapper from '@/components/page-wrapper/page-wrapper-component';
import PermissionWrapper from '@/components/permission-wrapper/permission-wrapper-component';
import StatusIndicator from '@/components/status-indicator/status-indicator-component';
import ViewEditDelete from '@/components/table/components/view-edit-delete/view-edit-delete';
import Table from '@/components/table/table';
import { useLazyQuery, useMutation } from '@/hooks/use-api';
import uploadToTempS3File from '@/utils/upload-to-s3-file';
import { useCallback, useState, useEffect } from 'react';
import { FiEdit } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import { MdOutlineDragIndicator } from 'react-icons/md';
import Styled from './product-categories-all-style';
import ProductCategoryAddEditForm from './product-category-add-edit-form';
import ReorderProductCategories from './reorder-product-categories/reorder-product-categories';

/**
 * The Product Categories Table that shows all the Product Categories.
 * @returns {JSX.Element} The Product Categories component to display all the Product Categories
 */
const ProductCategoriesAll = () => {
  const { id: companyId } = useParams();
  const tableId = 'productCategoriesTable';
  const [fetchingPresets, setFetchingPresets] = useState(true);
  const [defaultVariables, setDefaultVariables] = useState({});
  const [defaultSort, setDefaultSort] = useState('created_at:DESC');
  const [modalAction, setModalAction] = useState({ label: '', id: null });
  const [s3Loading, setS3Loading] = useState(false);
  const [defaultQuery, setDefaultQuery] = useState(undefined);
  const [openReorderProductCategoryModal, setOpenReorderProductCategoryModal] =
    useState(false);
  const getAllProductCategories = useCallback(
    (query) => getProductCategoriesApi(companyId, query),
    [companyId],
  );

  // API calls
  const {
    data: allProductCategoriesData,
    count,
    error: allProductCategoriesError,
    refetch,
    loading: getProductCategoriesLoading,
  } = useLazyQuery(getAllProductCategories);

  const refetchAllProductCategories = useCallback(
    (query) => {
      setDefaultQuery(query);
      refetch(query);
    },
    [refetch],
  );

  const [createProductCategory, { loading: createProductCategoryLoading }] =
    useMutation(createProductCategoryApi);
  const [updateProductCategory, { loading: updateProductCategoryLoading }] =
    useMutation(updateProductCategoryApi);
  const [deleteProductCategory, { loading: deleteProductCategoryLoading }] =
    useMutation(deleteProductCategoryApi);

  const getTablePresets = useCallback(() => {
    const tablePreferences = localStorage.getItem('tablePreferences');
    if (tablePreferences) {
      const tableConfigs = JSON.parse(tablePreferences)[tableId];

      if (tableConfigs) {
        const { query } = tableConfigs;

        if (query.length > 0) {
          // Get cached search value and apply it
          const searchQuery = query?.filter((q) => q.includes('search='));
          if (searchQuery?.length > 0) {
            const tempSearch = searchQuery[0].replace('search=', '');
            setDefaultVariables({ ...defaultVariables, search: tempSearch });
          }

          // Get cached sort value and apply it
          const sortQuery = query?.filter((q) => q.includes('sort='));
          if (sortQuery?.length > 0) {
            const tempSort = sortQuery[0].replace('sort=', '');
            setDefaultSort(tempSort);
          }

          // Get cached page size and apply it
          const pageSizeQuery = query?.filter((q) => q.includes('limit='));
          if (pageSizeQuery?.length > 0) {
            const tempPageSize = parseInt(
              pageSizeQuery[0].replace('limit=', ''),
              10,
            );
            setDefaultVariables({ ...defaultVariables, limit: tempPageSize });
          }
        }
      }
    }
    setFetchingPresets(false);

    // This is disabled because we don't want to re-run this function when the defaultVariables changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // USE EFFECTS
  useEffect(() => {
    getTablePresets();
  }, [getTablePresets]);

  const closeCreateEditModal = () => {
    setModalAction({ label: '', id: null });
  };

  const launchDeleteConfirmation = useCallback(
    (productCategoryData) => {
      ConfirmationModal({
        title: 'Delete Product Category',
        text: `Are you sure you want to delete ${productCategoryData.original.name}?`,
        onConfirm: () => {
          deleteProductCategory(
            companyId,
            productCategoryData?.original?.id,
          ).then(() => {
            refetch(defaultQuery).then(() => {
              Alert({
                icon: 'success',
                text: 'Product Category deleted successfully',
              });
            });
          });
        },
      });
    },
    [companyId, deleteProductCategory, refetch, defaultQuery],
  );

  const renderStatusPill = useCallback(({ row }) => {
    const { status } = row.original;
    return (
      <Styled.PillContainer>
        <StatusIndicator status={status} />
      </Styled.PillContainer>
    );
  }, []);

  const handleEdit = useCallback(
    (row) => {
      const productCategoryDetails = row.original;
      setModalAction({ label: 'Edit', id: productCategoryDetails.id });
    },
    [setModalAction],
  );

  const handleDelete = useCallback(
    (row) => {
      launchDeleteConfirmation(row);
    },
    [launchDeleteConfirmation],
  );

  const Actions = useCallback(
    ({ row }) => (
      <ViewEditDelete
        feature="productCategory"
        onEdit={() => handleEdit(row)}
        onDelete={() => handleDelete(row)}
        onView={null}
        onDuplicate={null}
      />
    ),
    [handleDelete, handleEdit],
  );

  const loading =
    getProductCategoriesLoading ||
    deleteProductCategoryLoading ||
    createProductCategoryLoading ||
    s3Loading ||
    updateProductCategoryLoading;

  const handleCreateProductCategory = (newFormData) => {
    if (
      newFormData.image_thumbnail &&
      typeof newFormData.image_thumbnail === 'object'
    ) {
      setS3Loading(true);
      uploadToTempS3File(newFormData?.image_thumbnail).then((data) => {
        const updatedFormData = {
          ...newFormData,
          image_thumbnail: data.url,
        };
        setS3Loading(false);
        createProductCategory(companyId, updatedFormData)
          .then(() => {
            setS3Loading(false);
            closeCreateEditModal();
            Alert({
              icon: 'success',
              text: 'Product Category created successfully',
            });
            refetch(defaultQuery);
          })
          .catch(() => {
            setS3Loading(false);
            Alert({
              icon: 'error',
              text: 'There was a problem creating your Product Category',
            });
          });
      });
    } else {
      createProductCategory(companyId, newFormData)
        .then(() => {
          closeCreateEditModal();
          Alert({
            icon: 'success',
            text: 'Product Category created successfully',
          });
          refetch(defaultQuery);
        })
        .catch(() => {
          Alert({
            icon: 'error',
            text: 'There was a problem creating your product category',
          });
        });
    }
  };

  const handleUpdateProductCategory = (newFormData) => {
    if (
      newFormData?.image_thumbnail &&
      typeof newFormData?.image_thumbnail === 'object'
    ) {
      setS3Loading(true);
      uploadToTempS3File(newFormData.image_thumbnail).then((data) => {
        const updatedFormData = {
          ...newFormData,
          image_thumbnail: data.url,
        };
        setS3Loading(false);
        updateProductCategory(companyId, updatedFormData)
          .then(() => {
            closeCreateEditModal();
            Alert({
              icon: 'success',
              text: 'Product Category updated successfully',
            });
            refetch(defaultQuery);
          })
          .catch(() => {
            setS3Loading(false);
            Alert({
              icon: 'error',
              text: 'There was a problem updating your Product Category',
            });
          });
      });
    } else {
      const submitData = newFormData;
      delete submitData.image_thumbnail;
      updateProductCategory(companyId, newFormData)
        .then(() => {
          closeCreateEditModal();
          Alert({
            icon: 'success',
            text: 'Product Category updated successfully',
          });
          refetch(defaultQuery);
        })
        .catch(() => {
          Alert({
            icon: 'error',
            text: 'There was a problem updating your Product Category',
          });
        });
    }
  };

  const onSubmitForm = (formData) => {
    const newFormData = {
      ...formData,
      company_id: companyId,
    };
    if (modalAction.label === 'Edit') {
      handleUpdateProductCategory(newFormData);
    } else {
      handleCreateProductCategory(newFormData);
    }
  };

  const renderIcon = (row) =>
    row?.cell?.value ? (
      <Styled.Thumbnail src={row.cell.value} alt="thumbnail" />
    ) : (
      <Styled.IconContainer>
        <Styled.NoThumbnail />
      </Styled.IconContainer>
    );

  const ProductCategoriesColumns = [
    {
      Header: 'IMAGE',
      id: 'image_thumbnail',
      accessor: 'image_thumbnail',
      search: true,
      Cell: renderIcon,
    },
    {
      Header: 'Name',
      id: 'name',
      accessor: 'name',
      search: true,
    },
    {
      Header: 'DESCRIPTION',
      id: 'description',
      accessor: 'description',
      search: true,
    },
    {
      Header: 'PRODUCTS',
      id: 'products',
      accessor: 'products',
    },
    {
      Header: 'STATUS',
      id: 'status',
      Cell: renderStatusPill,
    },
    {
      Header: 'ACTIONS',
      id: 'actions',
      Cell: Actions,
    },
  ];

  return (
    <PageWrapper>
      <Table
        feature="productCategory"
        onRowEdit={(row) => handleEdit(row)}
        onRowDelete={(row) => handleDelete(row)}
        columns={ProductCategoriesColumns}
        data={allProductCategoriesData ?? []}
        tableOptions={['search', 'columns']}
        id={tableId}
        defaultSort={defaultSort}
        defaultVariables={defaultVariables}
        dataCount={count ?? 0}
        error={allProductCategoriesError}
        loading={loading || fetchingPresets}
        filters={[]}
        refetch={refetchAllProductCategories}
        key={companyId}
        getDownloadData={getProductCategoriesApi}
        tableHeaderProps={
          <PermissionWrapper permissions={['productCategory.create']}>
            <PrimaryButton
              title="New Category"
              onClick={() => setModalAction({ label: 'Add', id: null })}
              buttonHeight="40px"
              icon={<FiEdit />}
              margin="0 0 0 6px"
            />
          </PermissionWrapper>
        }
        additionalHeaderButtons={[
          {
            title: 'Reorder Categories ',
            icon: MdOutlineDragIndicator,
            onClick: () => {
              setOpenReorderProductCategoryModal(true);
            },
          },
        ]}
      />
      <CreateEditModal
        open={!!modalAction.label}
        close={closeCreateEditModal}
        width="30%"
        alignItems="flex-start"
        title={`${modalAction.label} Category`}
        titleFontSize="28px"
        bodyStyle={{ width: '100%', padding: 0 }}
      >
        <FormWrapper
          onSubmit={onSubmitForm}
          defaultValues={async () => {
            const productCategoryId = modalAction.id;
            if (!productCategoryId) return {};
            const productCategory = await getProductCategoryApi(
              companyId,
              productCategoryId,
            );
            const { data: productCategoryData } = productCategory;
            return productCategoryData;
          }}
        >
          <ProductCategoryAddEditForm
            setModalAction={setModalAction}
            modalAction={modalAction}
            loading={loading}
          />
        </FormWrapper>
      </CreateEditModal>
      <CreateEditModal
        width="75vw"
        open={openReorderProductCategoryModal}
        height="75vh"
        titleFontSize="32px"
        showClose={false}
        bodyStyle={{
          padding: '0px 32px',
        }}
        headerPadding="32px 17px"
        close={() => {
          setOpenReorderProductCategoryModal(false);
        }}
        title="Reorder Product Categories"
        createPortal="reorderProductPortal"
      >
        <ReorderProductCategories
          open={openReorderProductCategoryModal}
          onClose={() => {
            setOpenReorderProductCategoryModal(false);
          }}
        />
      </CreateEditModal>
    </PageWrapper>
  );
};

export default ProductCategoriesAll;
